import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Layout } from '@/components/ui/Layout';
import { supabase } from '@/lib/supabase';
import {
  Loader2,
  Users,
  Building,
  FileText,
  BarChart2,
  ChevronRight,
  Search as SearchIcon,
  Home,
  Library,
  HelpCircle,
  Settings,
  BookOpen
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface SearchResult {
  type: 'page' | 'user' | 'organization' | 'resource' | 'report';
  id: string;
  title: string;
  description?: string;
  url: string;
  metadata?: Record<string, any>;
}

// Define static pages for search
const STATIC_PAGES = [
  {
    id: 'home',
    title: 'Home',
    description: 'Dashboard overview with key metrics and insights',
    url: '/',
    icon: Home
  },
  {
    id: 'library',
    title: 'Library',
    description: 'Access AI implementation guides, documentation, and resources',
    url: '/library',
    icon: Library
  },
  {
    id: 'reports',
    title: 'Reports',
    description: 'Analytics and performance metrics for AI systems',
    url: '/reports',
    icon: BarChart2
  },
  {
    id: 'users',
    title: 'Users',
    description: 'Manage user accounts and permissions',
    url: '/users',
    icon: Users
  },
  {
    id: 'organizations',
    title: 'Organizations',
    description: 'Manage organizations and their settings',
    url: '/organizations',
    icon: Building
  },
  {
    id: 'support',
    title: 'Support',
    description: 'Get help and contact support team',
    url: '/support',
    icon: HelpCircle
  },
  {
    id: 'settings',
    title: 'Settings',
    description: 'Configure account and application settings',
    url: '/settings',
    icon: Settings
  }
];

export function Search() {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function performSearch() {
      if (!query.trim()) {
        setResults([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Search static pages
        const pageResults = STATIC_PAGES.filter(page => 
          page.title.toLowerCase().includes(query.toLowerCase()) ||
          page.description.toLowerCase().includes(query.toLowerCase())
        ).map(page => ({
          type: 'page' as const,
          id: page.id,
          title: page.title,
          description: page.description,
          url: page.url,
          metadata: { icon: page.icon }
        }));

        // Search users
        const { data: users, error: usersError } = await supabase
          .from('users')
          .select(`
            id,
            email,
            organization_users (
              organizations (
                id,
                name
              )
            )
          `)
          .ilike('email', `%${query}%`)
          .limit(5);

        if (usersError) throw usersError;

        // Search organizations
        const { data: organizations, error: orgsError } = await supabase
          .from('organizations')
          .select('*')
          .or(`name.ilike.%${query}%, admin_email.ilike.%${query}%, admin_name.ilike.%${query}%`)
          .limit(5);

        if (orgsError) throw orgsError;

        // Search library resources
        const { data: resources, error: resourcesError } = await supabase
          .from('library_resources')
          .select('*')
          .or(`title.ilike.%${query}%, description.ilike.%${query}%`)
          .limit(5);

        if (resourcesError) throw resourcesError;

        // Search reports (engagement scores)
        const { data: reports, error: reportsError } = await supabase
          .from('engagement_scores')
          .select(`
            *,
            organizations (
              name
            )
          `)
          .gte('date', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString())
          .limit(5);

        if (reportsError) throw reportsError;

        // Combine and format results
        const formattedResults: SearchResult[] = [
          // Add page results first
          ...pageResults,

          // Format user results
          ...(users || []).map(user => ({
            type: 'user',
            id: user.id,
            title: user.email,
            description: user.organization_users?.[0]?.organizations?.name || 'No organization',
            url: '/users',
            metadata: { email: user.email }
          })),

          // Format organization results
          ...(organizations || []).map(org => ({
            type: 'organization',
            id: org.id,
            title: org.name,
            description: org.admin_name || org.admin_email || 'No admin contact',
            url: '/organizations',
            metadata: { admin_email: org.admin_email }
          })),

          // Format resource results
          ...(resources || []).map(resource => ({
            type: 'resource',
            id: resource.id,
            title: resource.title,
            description: resource.description,
            url: '/library',
            metadata: { type: resource.type }
          })),

          // Format report results
          ...(reports || []).map(report => ({
            type: 'report',
            id: report.id,
            title: `${report.organizations?.name} Report`,
            description: `Engagement Score: ${report.score}% on ${new Date(report.date).toLocaleDateString()}`,
            url: '/reports',
            metadata: { score: report.score, date: report.date }
          }))
        ];

        setResults(formattedResults);
      } catch (err) {
        console.error('Search error:', err);
        setError('Failed to perform search. Please try again.');
      } finally {
        setLoading(false);
      }
    }

    performSearch();
  }, [query]);

  const getIcon = (result: SearchResult) => {
    if (result.type === 'page' && result.metadata?.icon) {
      const Icon = result.metadata.icon;
      return <Icon className="h-5 w-5 text-blue-500 dark:text-blue-400" />;
    }

    switch (result.type) {
      case 'user':
        return <Users className="h-5 w-5 text-blue-500 dark:text-blue-400" />;
      case 'organization':
        return <Building className="h-5 w-5 text-green-500 dark:text-green-400" />;
      case 'resource':
        return <FileText className="h-5 w-5 text-purple-500 dark:text-purple-400" />;
      case 'report':
        return <BarChart2 className="h-5 w-5 text-orange-500 dark:text-orange-400" />;
      default:
        return <BookOpen className="h-5 w-5 text-blue-500 dark:text-blue-400" />;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400 dark:text-gray-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center space-x-2 mb-6">
          <SearchIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
          <h2 className="text-lg text-gray-600 dark:text-gray-300">
            Search results for "{query}"
          </h2>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {results.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 text-center">
            <p className="text-gray-500 dark:text-gray-400">No results found for "{query}"</p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
              Try adjusting your search terms or browse through the categories
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {results.map((result) => (
              <Link
                key={`${result.type}-${result.id}`}
                to={result.url}
                className="block bg-white dark:bg-gray-800 rounded-lg shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {getIcon(result)}
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                          {result.title}
                        </h3>
                        {result.description && (
                          <p className="text-sm text-gray-500 dark:text-gray-400">{result.description}</p>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  </div>
                  <div className="mt-2">
                    <span className={cn(
                      "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
                      {
                        'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300': result.type === 'page' || result.type === 'user',
                        'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300': result.type === 'organization',
                        'bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300': result.type === 'resource',
                        'bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-300': result.type === 'report',
                      }
                    )}>
                      {result.type.charAt(0).toUpperCase() + result.type.slice(1)}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}